/**
 * Onboarding step one: where your money goes.
 *
 * Skippable, on purpose. Nobody needs a bank account to record their first
 * voice note, and asking for one at the door costs contributors who would
 * otherwise have joined. The question is asked again at redeem, which is
 * the point at which it actually matters — and that same prompt is what
 * catches everybody who joined before any of this existed.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { OnboardingSteps } from "../components/OnboardingSteps";
import { PayoutDetailsForm } from "../components/PayoutDetailsForm";
import { useToast } from "../hooks/useToast";
import { onboarding as onboardingApi } from "../api/endpoints";
import { useAuth } from "../store/auth";
import { useConfig } from "../store/config";

export function OnboardingProfile() {
  const contributor = useAuth((state) => state.contributor);
  const setContributor = useAuth((state) => state.setContributor);
  const threshold = useConfig((state) => state.config.compensation.payout_threshold);
  const navigate = useNavigate();
  const toast = useToast();

  const [skipping, setSkipping] = useState(false);

  async function skip() {
    setSkipping(true);
    try {
      const result = await onboardingApi.skipPayoutDetails();
      setContributor(result.contributor);
      navigate("/onboarding/finish");
    } catch (err) {
      toast.show((err as Error).message, "error");
      setSkipping(false);
    }
  }

  return (
    <Layout showNav={false} title="Set Up">
      <section className="section">
        <div className="wrap narrow">
          <OnboardingSteps current="profile" />
          <div className="eyebrow">Step 01</div>
          <h1 className="display" style={{ fontSize: "clamp(26px,5vw,40px)" }}>
            Where should we<br />pay you?
          </h1>
          <p className="lede">
            We pay per accepted voice note, by bank transfer through Wise. You'll need
            this before you can take money out — but you can start recording now and
            add it later.
          </p>

          <PayoutDetailsForm
            defaultCountry={contributor?.country}
            submitLabel="Save and continue →"
            onSaved={() => {
              toast.show("Payout details saved.", "success");
              navigate("/onboarding/finish");
            }}
            secondary={
              <button
                type="button"
                className="btn btn-ghost"
                disabled={skipping}
                onClick={() => void skip()}
              >
                {skipping ? "One moment…" : "I'll do this later"}
              </button>
            }
          />

          <div className="hint" style={{ textAlign: "center" }}>
            Nothing is paid out until you've earned ${threshold.formatted}, so there's
            no rush — we'll ask again when you're ready to collect.
          </div>
        </div>
      </section>
    </Layout>
  );
}
